import React from 'react';

import {useTranslation} from '@aboutyou/i18n';
import {
  LoadingButton,
  ModalDialog,
  SecondaryContentPopperButton,
  Text,
} from '@aboutyou/ui-components';
import styled from 'styled-components';
import {useDeleteWishlistPageItem} from '../hooks/useWishlistPageStream';

function handlePreventClickEvent(event?: React.MouseEvent) {
  event?.stopPropagation();
  event?.preventDefault();
}

interface RemoveItemConfirmModalProps {
  isOpen: boolean;
  onClose: VoidFunction;
  onClickConfirm: VoidFunction;
  isLoading: boolean;
  onClickRemoveItemSuccess: VoidFunction;
  onClickRemoveItemError: VoidFunction;
  productId: number;
}

export const RemoveItemConfirmModal = ({
  isOpen,
  onClose,
  onClickConfirm,
  isLoading,
  onClickRemoveItemError,
  onClickRemoveItemSuccess,
  productId,
}: RemoveItemConfirmModalProps) => {
  const {t} = useTranslation();

  // this hook is expensive, only scoping it to the remove item button will reduce expensive re-render due to changes on the wishlist
  const onDelete = useDeleteWishlistPageItem();
  const handleClickButton = async () => {
    onClickConfirm();
    const success = await onDelete(productId);
    if (success) {
      onClickRemoveItemSuccess();
    } else {
      onClickRemoveItemError();
    }
  };

  return (
    <ModalDialog
      renderInPortal
      bodyContent={
        <Text size="px16" textAlignment="center">
          {t('Do you really want to remove this product from your wishlist?')}
        </Text>
      }
      footerContent={
        <>
          <SecondaryContentPopperButton
            label={t('Cancel')}
            onClick={e => {
              handlePreventClickEvent(e);
              onClose();
            }}
          />
          <StyledLoadingButton
            data-testid="WishlistConfirmDeletionButton"
            isLoading={isLoading}
            disableShadow
            onClick={e => {
              handlePreventClickEvent(e);
              handleClickButton();
            }}
          >
            {t('Remove')}
          </StyledLoadingButton>
        </>
      }
      onClose={onClose}
      open={isOpen}
    />
  );
};

const StyledLoadingButton = styled(LoadingButton)`
  padding-inline: 0px;
`;
